import { forwardRef } from "react";

const ServiceBillPreview = forwardRef(({ formData, serviceTotal, partsTotal, tax, total }, ref) => {
  const fmt = (v) => new Intl.NumberFormat("en-IN").format(v || 0);
  const fmtDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return d; }
  };

  const laborCharges = parseFloat(formData.laborCharges) || 0;
  const discount = parseFloat(formData.discount) || 0;
  const hasServiceItems = (formData.serviceItems || []).some((i) => i.description);
  const hasPartsItems = (formData.partsItems || []).some((i) => i.partName);

  return (
    <div
      ref={ref}
      className="print-document bg-white text-black mx-auto"
      style={{
        fontFamily: "Arial, sans-serif",
        fontSize: "11px",
        width: "100%",
        maxWidth: "210mm",
        padding: "12mm",
        boxSizing: "border-box",
        minHeight: "277mm",
        color: "#000",
        background: "#fff",
      }}
    >
      {/* ── Header ──────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #000", paddingBottom: "8px", marginBottom: "8px" }}>
        <img
          src="https://customer-assets.emergentagent.com/job_384f5a02-e73c-441b-8711-7b4aa811af91/artifacts/1llnvk1o_be6ba909-56b0-4515-9de1-7b59366d1e68_Untitled.avif"
          alt="Logo" style={{ height: "91px", objectFit: "contain" }}
          onError={(e) => { e.target.style.display = "none"; }}
        />
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "24px", fontWeight: "bold" }}>SHREE MOBILITY TRADE</div>
          <div style={{ fontSize: "10px", color: "#555" }}>(Authorised Service Center - Kinetic Green)</div>
          <div style={{ fontSize: "10px" }}>NH 14, Brahmanigram more, Rampurhat</div>
          <div style={{ fontSize: "10px", fontWeight: "600" }}>Mob: 9749430705 / 7063511705</div>
          <div style={{ fontSize: "10px", fontWeight: "600" }}>GSTIN: 19AFQFS8958E1Z2</div>
        </div>
      </div>

      {/* ── Doc Title ────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
        <div style={{ fontSize: "10px", lineHeight: "1.7" }}>
          <div><b>Bill No.:</b> {formData.billNumber}</div>
          <div><b>Date:</b> {fmtDate(formData.billDate)}</div>
          {formData.serviceType && <div><b>Service Type:</b> {formData.serviceType}</div>}
        </div>
        <div style={{ border: "2px solid #000", padding: "4px 16px", fontWeight: "bold", fontSize: "14px" }}>SERVICE BILL</div>
      </div>

      {/* ── Customer & Vehicle ───────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
        <div style={{ border: "1px solid #000", padding: "6px" }}>
          <div style={{ fontWeight: "bold", background: "#f3f3f3", margin: "-6px -6px 4px -6px", padding: "3px 6px", borderBottom: "1px solid #000", fontSize: "10px" }}>CUSTOMER DETAILS</div>
          <div style={{ lineHeight: "1.7", fontSize: "10px" }}>
            <div><b>Name:</b> {formData.customerName || "—"}</div>
            <div><b>Phone:</b> {formData.customerPhone || "—"}</div>
            <div><b>Address:</b> {formData.customerAddress || "—"}</div>
          </div>
        </div>
        <div style={{ border: "1px solid #000", padding: "6px" }}>
          <div style={{ fontWeight: "bold", background: "#f3f3f3", margin: "-6px -6px 4px -6px", padding: "3px 6px", borderBottom: "1px solid #000", fontSize: "10px" }}>VEHICLE DETAILS</div>
          <div style={{ lineHeight: "1.7", fontSize: "10px" }}>
            <div><b>Model:</b> {formData.vehicleModel || "—"}</div>
            <div><b>Reg. No:</b> {formData.vehicleNumber || "—"}</div>
            <div><b>Chassis:</b> {formData.chassisNumber || "—"}</div>
            <div><b>KM Reading:</b> {formData.kmReading || "—"}</div>
          </div>
        </div>
      </div>

      {/* ── Complaints ───────────────────────────────────────────── */}
      {formData.complaints && (
        <div style={{ background: "#fffbeb", border: "1px solid #000", padding: "5px 8px", marginBottom: "8px", fontSize: "10px" }}>
          <b>COMPLAINTS: </b>{formData.complaints}
        </div>
      )}

      {/* ── Service Items Table ──────────────────────────────────── */}
      {hasServiceItems && (
        <div style={{ marginBottom: "8px" }}>
          <div style={{ fontWeight: "bold", marginBottom: "3px", fontSize: "10px" }}>SERVICE WORK</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px" }}>
            <thead>
              <tr style={{ background: "#f3f3f3" }}>
                <th style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "left", width: "24px" }}>#</th>
                <th style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "left" }}>Description</th>
                <th style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "center", width: "36px" }}>Qty</th>
                <th style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "right", width: "60px" }}>Rate</th>
                <th style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "right", width: "72px" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {(formData.serviceItems || []).filter((i) => i.description).map((item, idx) => (
                <tr key={idx}>
                  <td style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "center" }}>{idx + 1}</td>
                  <td style={{ border: "1px solid #000", padding: "3px 5px" }}>{item.description}</td>
                  <td style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "center" }}>{item.quantity}</td>
                  <td style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "right" }}>₹{fmt(item.rate)}</td>
                  <td style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "right", fontWeight: "600" }}>₹{fmt(item.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: "#f9f9f9" }}>
                <td colSpan={4} style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "right", fontWeight: "600" }}>Service Total</td>
                <td style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "right", fontWeight: "600" }}>₹{fmt(serviceTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* ── Parts Table ──────────────────────────────────────────── */}
      {hasPartsItems && (
        <div style={{ marginBottom: "8px" }}>
          <div style={{ fontWeight: "bold", marginBottom: "3px", fontSize: "10px" }}>SPARE PARTS</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px" }}>
            <thead>
              <tr style={{ background: "#f3f3f3" }}>
                <th style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "left", width: "24px" }}>#</th>
                <th style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "left" }}>Part Name</th>
                <th style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "left", width: "70px" }}>Part No.</th>
                <th style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "center", width: "36px" }}>Qty</th>
                <th style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "right", width: "55px" }}>Rate</th>
                <th style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "right", width: "72px" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {(formData.partsItems || []).filter((i) => i.partName).map((item, idx) => (
                <tr key={idx}>
                  <td style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "center" }}>{idx + 1}</td>
                  <td style={{ border: "1px solid #000", padding: "3px 5px" }}>{item.partName}</td>
                  <td style={{ border: "1px solid #000", padding: "3px 5px" }}>{item.partNumber || "—"}</td>
                  <td style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "center" }}>{item.quantity}</td>
                  <td style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "right" }}>₹{fmt(item.rate)}</td>
                  <td style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "right", fontWeight: "600" }}>₹{fmt(item.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: "#f9f9f9" }}>
                <td colSpan={5} style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "right", fontWeight: "600" }}>Parts Total</td>
                <td style={{ border: "1px solid #000", padding: "3px 5px", textAlign: "right", fontWeight: "600" }}>₹{fmt(partsTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* ── Summary ──────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
        <table style={{ borderCollapse: "collapse", fontSize: "10px", width: "220px" }}>
          <tbody>
            <tr><td style={{ border: "1px solid #000", padding: "3px 8px" }}>Service Charges</td><td style={{ border: "1px solid #000", padding: "3px 8px", textAlign: "right" }}>₹{fmt(serviceTotal)}</td></tr>
            <tr><td style={{ border: "1px solid #000", padding: "3px 8px" }}>Parts Charges</td><td style={{ border: "1px solid #000", padding: "3px 8px", textAlign: "right" }}>₹{fmt(partsTotal)}</td></tr>
            {laborCharges > 0 && <tr><td style={{ border: "1px solid #000", padding: "3px 8px" }}>Labor Charges</td><td style={{ border: "1px solid #000", padding: "3px 8px", textAlign: "right" }}>₹{fmt(laborCharges)}</td></tr>}
            {tax > 0 && <tr><td style={{ border: "1px solid #000", padding: "3px 8px" }}>GST (CGST + SGST)</td><td style={{ border: "1px solid #000", padding: "3px 8px", textAlign: "right" }}>₹{fmt(tax)}</td></tr>}
            {discount > 0 && <tr style={{ color: "#c00" }}><td style={{ border: "1px solid #000", padding: "3px 8px" }}>Discount</td><td style={{ border: "1px solid #000", padding: "3px 8px", textAlign: "right" }}>−₹{fmt(discount)}</td></tr>}
          </tbody>
          <tfoot>
            <tr style={{ background: "#f3f3f3" }}>
              <td style={{ border: "2px solid #000", padding: "5px 8px", fontWeight: "bold", fontSize: "12px" }}>GRAND TOTAL</td>
              <td style={{ border: "2px solid #000", padding: "5px 8px", textAlign: "right", fontWeight: "bold", fontSize: "12px" }}>₹{fmt(total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ── Payment & Technician ─────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
        <div style={{ border: "1px solid #000", padding: "6px", fontSize: "10px" }}>
          <div style={{ fontWeight: "bold", marginBottom: "3px" }}>PAYMENT</div>
          <div><b>Mode:</b> {formData.paymentMode || "Cash"}</div>
        </div>
        <div style={{ border: "1px solid #000", padding: "6px", fontSize: "10px" }}>
          <div style={{ fontWeight: "bold", marginBottom: "3px" }}>SERVICE BY</div>
          <div style={{ fontWeight: "600" }}>{formData.technicianName || "—"}</div>
        </div>
      </div>

      {/* ── Notes ────────────────────────────────────────────────── */}
      {formData.notes && (
        <div style={{ background: "#f9f9f9", border: "1px solid #000", padding: "5px 8px", marginBottom: "8px", fontSize: "10px" }}>
          <b>NOTES: </b>{formData.notes}
        </div>
      )}

      {/* ── Signatures ───────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", paddingTop: "16px" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ borderTop: "1px solid #000", width: "140px", paddingTop: "3px", fontSize: "10px" }}>Customer's Signature</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "10px", fontWeight: "600", marginBottom: "20px" }}>For, SHREE MOBILITY TRADE</div>
          <div style={{ borderTop: "1px solid #000", width: "140px", paddingTop: "3px", fontSize: "10px" }}>Authorised Signatory</div>
        </div>
      </div>
    </div>
  );
});

ServiceBillPreview.displayName = "ServiceBillPreview";
export default ServiceBillPreview;
